import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useTurnstile, TurnstileMount } from "@/components/TurnstileWidget";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const turnstile = useTurnstile();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password, {
        turnstile_token: turnstile.token,
        _hp_website: honeypot,
      });
      toast.success("Welcome back");
      navigate(user.role === "business" ? "/dashboard/business" : "/dashboard/customer");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      turnstile.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20">
      <h1 className="font-display text-4xl tracking-tight">Log in</h1>
      <p className="mt-2 text-gray-600">Continue managing your reputation.</p>

      <form className="mt-10 space-y-5" onSubmit={submit}>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm mt-1" data-testid="login-email-input" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/forgot-password"
              className="text-sm text-[#B31E1E] hover:underline font-medium"
              data-testid="login-forgot-password"
            >
              Forgot password?
            </Link>
          </div>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm mt-1" data-testid="login-password-input" />
        </div>
        {/* Honeypot — invisible to humans, catnip to bots */}
        <input
          type="text"
          name="_hp_website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
          data-testid="login-honeypot"
        />
        <TurnstileMount widgetRef={turnstile.widgetRef} sitekey={turnstile.sitekey} />
        <Button type="submit" className="w-full rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" disabled={loading} data-testid="login-submit-button">
          {loading ? "Signing in..." : "Sign in"}
        </Button>
        <p className="text-center text-sm text-gray-600">
          Trouble signing in?{" "}
          <Link
            to="/forgot-password"
            className="text-[#B31E1E] hover:underline font-medium"
            data-testid="login-reset-password-cta"
          >
            Reset your password
          </Link>
        </p>
      </form>

      <p className="mt-6 text-sm text-gray-600">
        New here? <Link to="/register" className="text-[#B31E1E] hover:underline" data-testid="login-link-register">Create an account</Link>
      </p>
    </div>
  );
}
